import { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { getProfileImageUrl } from '@/lib/utils';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, Crown, Wallet, CheckCircle2, XCircle, Search, Plus, Minus, Flame, Sparkles, Radio, FileText, Trash2, Edit, Send, Upload, Music, Video, X, Link2, User, MessageSquare, Briefcase, Calendar, BarChart3, BookOpen, Shield, Palmtree, Loader2, Copy, Mail, Phone, Bell, Flag, Eye, RefreshCw, Clock, LogIn, Heart, Lock, ArrowRight, Download, Diamond, FlaskConical, Pause, Play, RotateCcw, TrendingUp } from 'lucide-react';
import AdminScriptures from '@/components/admin/AdminScriptures';
import AdminSafetyDashboard from '@/components/admin/AdminSafetyDashboard';
import AdminESignatures from '@/components/admin/AdminESignatures';
import AdminRetreatTab from '@/components/admin/AdminRetreatTab';
import AdminLegacyTab from '@/components/admin/AdminLegacyTab';
import AdminBillingTab from '@/components/admin/AdminBillingTab';
import AdminAnalyticsTab from '@/components/admin/AdminAnalyticsTab';
import AdminLibraryTab from '@/components/admin/AdminLibraryTab';
import AdminUsersManagement from '@/components/admin/AdminUsersManagement';
import AdminCoachesTab from '@/components/admin/AdminCoachesTab';
import AdminGroupSessionsTab from '@/components/admin/AdminGroupSessionsTab';
import AdminBroadcastTab from '@/components/admin/AdminBroadcastTab';
import FrontendErrorBadge from '@/components/admin/FrontendErrorBadge';
import ResetGiftFunnelWidget from '@/components/admin/ResetGiftFunnelWidget';
import SoulFoodPipelineStatus from '@/components/admin/SoulFoodPipelineStatus';
import AdminSoulFoodTab from '@/components/admin/AdminSoulFoodTab';
import AdminLeadsManagement from '@/components/admin/AdminLeadsManagement';
import AdminCircleModeration from '@/components/admin/AdminCircleModeration';
import AdminFunnelDashboard from '@/components/admin/AdminFunnelDashboard';
import AdminGiftApplicationsTab from '@/components/admin/AdminGiftApplicationsTab';
import AdminAffiliatesTab from '@/components/admin/AdminAffiliatesTab';
import AdminCommunicationsLog from '@/components/admin/AdminCommunicationsLog';
import AdminEmailTemplates from '@/components/admin/AdminEmailTemplates';

const API_URL = window.location.origin;

// Room types for retreat
const ROOM_TYPES = [
  { id: 'geodesic-dome', name: 'Sacred Geodesic Domes', price: '$5,297' },
  { id: 'bungalow-double', name: 'Exotic Jungle Bungalows (Double)', price: '$4,997' },
  { id: 'bungalow-single', name: 'Exotic Jungle Bungalows (Single)', price: '$5,997' },
  { id: 'luxury-villa', name: 'Luxurious Private Villas', price: '$6,597' }
];

// Get active tab from URL path
const getTabFromPath = (pathname) => {
  if (pathname.includes('/admin/analytics')) return 'analytics';
  if (pathname.includes('/admin/users')) return 'users';
  if (pathname.includes('/admin/leads')) return 'leads';
  if (pathname.includes('/admin/coaches')) return 'coaches';
  if (pathname.includes('/admin/group-sessions')) return 'group-sessions';
  if (pathname.includes('/admin/requests')) return 'requests';
  if (pathname.includes('/admin/coach-notes')) return 'coach-notes';
  if (pathname.includes('/admin/invoices')) return 'coach-invoices';
  if (pathname.includes('/admin/billing')) return 'billing';
  if (pathname.includes('/admin/messages')) return 'messages';
  if (pathname.includes('/admin/support')) return 'support';
  if (pathname.includes('/admin/content')) return 'content';
  if (pathname.includes('/admin/communities')) return 'communities';
  if (pathname.includes('/admin/safety')) return 'safety';
  if (pathname.includes('/admin/esign')) return 'esign';
  if (pathname.includes('/admin/retreat')) return 'retreat';
  if (pathname.includes('/admin/legacy')) return 'legacy';
  if (pathname.includes('/admin/collective')) return 'collective';
  if (pathname.includes('/admin/gift-applications')) return 'gift-applications';
  if (pathname.includes('/admin/affiliates')) return 'affiliates';
  if (pathname.includes('/admin/broadcast')) return 'broadcast';
  if (pathname.includes('/admin/communications')) return 'communications';
  if (pathname.includes('/admin/email-templates')) return 'email-templates';
  if (pathname.includes('/admin/soul-food')) return 'soul-food';
  return 'dashboard';
};

const AdminPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const audioInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [hotSeats, setHotSeats] = useState([]);
  const [concierge, setConcierge] = useState([]);
  const [transmissions, setTransmissions] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [coachNotes, setCoachNotes] = useState([]);
  const [coachInvoices, setCoachInvoices] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [adminStats, setAdminStats] = useState({ totalClients: 0, totalCoaches: 0, clientsByProgram: {} });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('');
  const [invoiceSortBy, setInvoiceSortBy] = useState('date'); // 'date', 'status', 'due', 'amount'
  const [notesSortBy, setNotesSortBy] = useState('date'); // 'date', 'coach', 'client'
  const [notesSearchTerm, setNotesSearchTerm] = useState('');
  const [notesCoachFilter, setNotesCoachFilter] = useState('');
  const [notesClientFilter, setNotesClientFilter] = useState('');

  // Messaging state
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  // VIP Sync state (for dashboard quick action)
  const [syncingVipCircles, setSyncingVipCircles] = useState(false);

  // Grant/Revoke Modal State
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTier, setSelectedTier] = useState('');
  const [walletAdjustment, setWalletAdjustment] = useState({ type: '', amount: 0, reason: '' });

  // Add to Retreat Dialog State
  const [showRetreatDialog, setShowRetreatDialog] = useState(false);
  const [retreatUser, setRetreatUser] = useState(null);
  const [retreatForm, setRetreatForm] = useState({
    room_type: 'geodesic-dome',
    is_vip: false,
    send_notifications: true
  });
  const [addingToRetreat, setAddingToRetreat] = useState(false);
  const [retreatResult, setRetreatResult] = useState(null);

  // Community Management State
  const [adminCommunities, setAdminCommunities] = useState([]);
  const [editingCommunity, setEditingCommunity] = useState(null);
  const [communityPosts, setCommunityPosts] = useState([]);
  const [selectedCommunityForPosts, setSelectedCommunityForPosts] = useState(null);
  const [showCreateCircleDialog, setShowCreateCircleDialog] = useState(false);
  const [newCircleName, setNewCircleName] = useState('');
  const [newCircleDescription, setNewCircleDescription] = useState('');
  const [newCircleCategory, setNewCircleCategory] = useState('Sister Circle');
  const [newCircleImage, setNewCircleImage] = useState('');
  const [newCirclePrivate, setNewCirclePrivate] = useState(false);
  const [creatingCircle, setCreatingCircle] = useState(false);

  // Collective Applications State
  const [collectiveApplications, setCollectiveApplications] = useState([]);
  const [collectiveStats, setCollectiveStats] = useState({});
  const [loadingCollective, setLoadingCollective] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applicationNote, setApplicationNote] = useState('');
  const [collectiveFilter, setCollectiveFilter] = useState(null); // 'total', 'new', 'inner_edge', 'inner_circle', 'converted'
  const [sendingSdcLinkId, setSendingSdcLinkId] = useState(null); // id of the app currently generating a link

  // Content Management State
  const [newTransmission, setNewTransmission] = useState({ title: '', content: '', audio_url: '', video_url: '', vimeo_url: '', youtube_url: '', media_type: null });
  const [newPrompt, setNewPrompt] = useState({ title: '', prompt_text: '' });
  const [showTransmissionForm, setShowTransmissionForm] = useState(false);
  const [showPromptForm, setShowPromptForm] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchData();
    fetchAdminStats();
    fetchAllUsers();
  }, [searchTerm, tierFilter, invoiceStatusFilter]);

  // Update active tab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  // Fetch messages when on messages tab
  useEffect(() => {
    if (activeTab === 'messages') {
      fetchAdminConversations();
    }
    if (activeTab === 'communities') {
      fetchAdminCommunities();
    }
    if (activeTab === 'collective') {
      fetchCollectiveApplications();
    }
  }, [activeTab]);

  const fetchCollectiveApplications = async () => {
    setLoadingCollective(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/collective-applications`);
      setCollectiveApplications(response.data.applications || []);
      setCollectiveStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching collective applications:', error);
    } finally {
      setLoadingCollective(false);
    }
  };

  // Helper to filter collective applications based on selected stat card
  const getFilteredApplications = () => {
    if (!collectiveFilter || collectiveFilter === 'total') return collectiveApplications;
    return collectiveApplications.filter((app) => {
      if (collectiveFilter === 'new') return !app.status || app.status === 'submitted' || app.status === 'new';
      if (collectiveFilter === 'inner_edge') return app.outcome === 'inner_edge';
      if (collectiveFilter === 'inner_circle') return app.outcome === 'inner_circle';
      if (collectiveFilter === 'converted') return app.purchase_status?.has_inner_circle || app.purchase_status?.has_inner_edge || app.purchase_status?.has_sdc;
      return true;
    });
  };

  const filteredCollectiveApplications = getFilteredApplications();

  const updateApplicationStatus = async (applicationId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/collective-applications/${applicationId}`, {
        status: newStatus
      });
      fetchCollectiveApplications();
      toast.success('Application status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const addApplicationNote = async (applicationId) => {
    if (!applicationNote.trim()) return;
    try {
      await axios.put(`${API_URL}/api/admin/collective-applications/${applicationId}`, {
        note: applicationNote
      });
      setApplicationNote('');
      fetchCollectiveApplications();
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const deleteCollectiveApplication = async (applicationId, applicantName) => {
    if (!window.confirm(`Are you sure you want to delete the application from ${applicantName}? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/admin/collective-applications/${applicationId}`);
      fetchCollectiveApplications();
      toast.success('Application deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete application');
    }
  };

  // Generate a hidden /sdc-payment/:token link for a Collective applicant and (optionally) email it.
  const handleSendSdcLinkForCollective = async (app) => {
    const email = app?.contact?.email;
    const name = app?.contact?.name;
    if (!email || !name) {
      toast.error('Application is missing name or email');
      return;
    }
    const sendEmail = window.confirm(
      `Send Sister Diamonds Collective payment link to ${name} (${email})?\n\nClick OK to also email them the link.\nClick Cancel to just generate the link without emailing.`
    );
    setSendingSdcLinkId(app.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/sdc-invitations`,
        {
          applicant_email: email,
          applicant_name: name,
          collective_application_id: app.id,
          send_email: sendEmail,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = res?.data?.invitation_url;
      if (url) {
        try {
          await navigator.clipboard.writeText(url);
          toast.success(
            sendEmail
              ? `Email queued to ${email}. Link also copied to clipboard.`
              : 'Link generated and copied to clipboard.',
            { duration: 8000 }
          );
        } catch {
          toast.success(`Link generated: ${url}`, { duration: 12000 });
        }
        window.prompt('SDC payment link (copy and share):', url);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create payment invitation');
    } finally {
      setSendingSdcLinkId(null);
    }
  };

  const fetchAdminStats = async () => {
    try {
      const usersRes = await axios.get(`${API_URL}/api/admin/users`);
      const allUsersData = usersRes.data || [];
      
      // Calculate stats
      const clients = allUsersData.filter(u => u.role !== 'admin');
      const coachUsers = allUsersData.filter(u => u.is_coach);
      
      // Count by program/tier
      const clientsByProgram = {
        inner_circle: allUsersData.filter(u => u.activeTiers?.includes('inner_circle')).length,
        inner_edge: allUsersData.filter(u => u.activeTiers?.includes('inner_edge')).length,
        coaching_activation: allUsersData.filter(u => u.activeTiers?.includes('coaching_activation')).length,
        sdc: allUsersData.filter(u => u.activeTiers?.includes('sdc')).length,
        costa_rica_retreat: allUsersData.filter(u => u.activeTiers?.includes('costa_rica_retreat')).length
      };
      
      setAdminStats({
        totalClients: clients.length,
        totalCoaches: coachUsers.length,
        clientsByProgram
      });
    } catch (error) {
      console.error('Failed to fetch admin stats:', error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users`);
      setAllUsers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch all users:', error);
    }
  };

  const handleSyncVipCircles = async () => {
    if (!window.confirm('This will sync all users with VIP-eligible tiers to their respective VIP circles. Continue?')) return;
    
    setSyncingVipCircles(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/sync-vip-circles`);
      toast.success(response.data.message || `Synced VIP circle memberships`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync VIP circles');
    } finally {
      setSyncingVipCircles(false);
    }
  };

  const fetchAdminConversations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/messages`);
      setConversations(res.data || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  };

  const fetchAdminCommunities = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/communities`);
      setAdminCommunities(res.data || []);
    } catch (error) {
      console.error('Failed to fetch communities:', error);
    }
  };

  const fetchCommunityPosts = async (communityId) => {
    try {
      const res = await axios.get(`${API_URL}/api/communities/${communityId}/posts`);
      setCommunityPosts(res.data || []);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  };

  const handleUpdateCommunity = async (communityId, updateData) => {
    try {
      await axios.put(`${API_URL}/api/admin/communities/${communityId}`, updateData);
      toast.success('Community updated successfully');
      setEditingCommunity(null);
      fetchAdminCommunities();
    } catch (error) {
      toast.error('Failed to update community');
    }
  };

  const handleDeletePost = async (communityId, postId) => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/communities/${communityId}/posts/${postId}`);
      toast.success('Post deleted');
      fetchCommunityPosts(communityId);
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleCreateCircle = async () => {
    if (!newCircleName.trim() || !newCircleDescription.trim()) {
      toast.error('Please fill in name and description');
      return;
    }
    
    setCreatingCircle(true);
    try {
      await axios.post(`${API_URL}/api/admin/circles`, {
        name: newCircleName,
        description: newCircleDescription,
        category: newCircleCategory,
        image_url: newCircleImage || null,
        is_private: newCirclePrivate
      });
      
      toast.success('Sister Circle created successfully');
      setShowCreateCircleDialog(false);
      setNewCircleName('');
      setNewCircleDescription('');
      setNewCircleCategory('Sister Circle');
      setNewCircleImage('');
      setNewCirclePrivate(false);
      fetchAdminCommunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create circle');
    } finally {
      setCreatingCircle(false);
    }
  };

  const handleDeleteCircle = async (circleId, circleName) => {
    if (!confirm(`Are you sure you want to delete "${circleName}"? This action cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/circles/${circleId}`);
      toast.success(`"${circleName}" deleted successfully`);
      fetchAdminCommunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete circle');
    }
  };

  const fetchMessagesWithUser = async (userId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/messages/${userId}`);
      setMessages(res.data || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleSendAdminMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedPartner) return;

    setSendingMessage(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/messages`, {
        receiver_id: selectedPartner.id,
        content: newMessage.trim()
      });
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      fetchAdminConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const fetchData = async () => {
    try {
      const [usersRes, bookingsRes, hotSeatsRes, conciergeRes, transmissionsRes, promptsRes, notesRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`, { params: { search: searchTerm, tier: tierFilter || undefined } }),
        axios.get(`${API_URL}/api/admin/bookings`),
        axios.get(`${API_URL}/api/admin/requests/hot-seat`),
        axios.get(`${API_URL}/api/admin/requests/concierge`),
        axios.get(`${API_URL}/api/admin/transmissions`),
        axios.get(`${API_URL}/api/admin/prompts`),
        axios.get(`${API_URL}/api/admin/coach-notes`),
        axios.get(`${API_URL}/api/admin/coach-invoices`, { params: { status: invoiceStatusFilter || undefined } })
      ]);
      
      setUsers(usersRes.data);
      setBookings(bookingsRes.data);
      setHotSeats(hotSeatsRes.data);
      setConcierge(conciergeRes.data);
      setTransmissions(transmissionsRes.data);
      setPrompts(promptsRes.data);
      setCoachNotes(notesRes.data);
      setCoachInvoices(invoicesRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantTier = async (userId) => {
    if (!selectedTier) {
      toast.error('Please select a tier');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/admin/tiers/grant`, {
        user_id: userId,
        tier: selectedTier,
        granted_by: user.id,
        notes: 'Granted via admin panel'
      });
      toast.success(`Granted ${selectedTier} to user`);
      setSelectedTier('');
      fetchData();
    } catch (error) {
      toast.error('Failed to grant tier');
    }
  };

  const handleRevokeTier = async (userId, tier) => {
    if (!confirm(`Are you sure you want to revoke ${tier}?`)) return;
    
    try {
      await axios.post(`${API_URL}/api/admin/tiers/revoke`, {
        user_id: userId,
        tier: tier,
        granted_by: user.id,
        notes: 'Revoked via admin panel'
      });
      toast.success(`Revoked ${tier}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke tier');
    }
  };

  const handleWalletAdjust = async (userId) => {
    if (!walletAdjustment.type || !walletAdjustment.amount || !walletAdjustment.reason) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/admin/wallet/adjust`, {
        user_id: userId,
        wallet_type: walletAdjustment.type,
        amount: parseInt(walletAdjustment.amount),
        reason: walletAdjustment.reason
      });
      toast.success('Wallet adjusted');
      setWalletAdjustment({ type: '', amount: 0, reason: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to adjust wallet');
    }
  };

  // ========== ADD TO RETREAT FUNCTIONS ==========
  
  const openRetreatDialog = (user) => {
    setRetreatUser(user);
    setRetreatForm({
      room_type: 'geodesic-dome',
      is_vip: false,
      send_notifications: true
    });
    setRetreatResult(null);
    setShowRetreatDialog(true);
  };

  const handleAddToRetreat = async () => {
    if (!retreatUser) return;
    
    setAddingToRetreat(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/retreat/add-attendee`,
        {
          email: retreatUser.email,
          full_name: retreatUser.full_name,
          phone_number: retreatUser.phone_number || '',
          room_type: retreatForm.room_type,
          is_vip: retreatForm.is_vip,
          send_notifications: retreatForm.send_notifications
        }
      );
      
      setRetreatResult(response.data);
      toast.success(`${retreatUser.full_name} added to retreat!`);
      fetchData();
    } catch (error) {
      console.error('Error adding to retreat:', error);
      toast.error(error.response?.data?.detail || 'Failed to add to retreat');
    } finally {
      setAddingToRetreat(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // ========== USER MANAGEMENT FUNCTIONS ==========
  

  const handleCompleteSession = async (bookingId) => {
    try {
      await axios.post(`${API_URL}/api/admin/session/complete?booking_id=${bookingId}`);
      toast.success('Session marked complete, wallet decremented');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete session');
    }
  };

  const handleUpdateRequest = async (type, requestId, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/requests/${type}/${requestId}?status=${status}`);
      toast.success('Request updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  // Invoice management functions
  const handleUpdateInvoice = async (invoiceId, updates) => {
    try {
      await axios.put(`${API_URL}/api/admin/invoices/${invoiceId}`, updates);
      toast.success('Invoice updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const handleDeleteInvoice = async (invoiceId, invoiceNumber) => {
    if (!window.confirm(`Are you sure you want to delete Invoice #${invoiceNumber}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  // Content Management Functions
  // Handle media file upload
  const handleMediaUpload = async (event, mediaType) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (100MB max)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 100MB.');
      return;
    }

    setUploadingMedia(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/admin/upload-media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      const { url, media_type } = response.data;
      
      if (media_type === 'audio') {
        setNewTransmission(prev => ({ ...prev, audio_url: url, video_url: '', media_type: 'audio' }));
      } else {
        setNewTransmission(prev => ({ ...prev, video_url: url, audio_url: '', media_type: 'video' }));
      }
      
      toast.success(`${media_type === 'audio' ? 'Audio' : 'Video'} uploaded successfully!`);
    } catch (error) {
      toast.error('Failed to upload file. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setUploadingMedia(false);
      setUploadProgress(0);
    }
  };

  const clearMedia = () => {
    setNewTransmission(prev => ({ ...prev, audio_url: '', video_url: '', vimeo_url: '', youtube_url: '', media_type: null }));
  };

  // Extract Vimeo video ID from URL
  const extractVimeoId = (url) => {
    if (!url) return null;
    const patterns = [
      /vimeo\.com\/(\d+)/,
      /player\.vimeo\.com\/video\/(\d+)/,
      /vimeo\.com\/video\/(\d+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Extract YouTube video ID from URL
  const extractYoutubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /youtube\.com\/watch\?v=([^&]+)/,
      /youtu\.be\/([^?]+)/,
      /youtube\.com\/embed\/([^?]+)/,
      /youtube\.com\/v\/([^?]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const handleVimeoUrlChange = (url) => {
    const vimeoId = extractVimeoId(url);
    if (vimeoId || url === '') {
      setNewTransmission(prev => ({ 
        ...prev, 
        vimeo_url: url,
        youtube_url: '',
        audio_url: '',
        video_url: '',
        media_type: url ? 'vimeo' : null 
      }));
    } else if (url) {
      toast.error('Please enter a valid Vimeo URL (e.g., https://vimeo.com/123456789)');
    }
  };

  const handleYoutubeUrlChange = (url) => {
    const youtubeId = extractYoutubeId(url);
    if (youtubeId || url === '') {
      setNewTransmission(prev => ({ 
        ...prev, 
        youtube_url: url,
        vimeo_url: '',
        audio_url: '',
        video_url: '',
        media_type: url ? 'youtube' : null 
      }));
    } else if (url) {
      toast.error('Please enter a valid YouTube URL (e.g., https://youtube.com/watch?v=...)');
    }
  };

  const handleCreateTransmission = async () => {
    if (!newTransmission.title || !newTransmission.content) {
      toast.error('Please fill in title and content');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/transmission`, {
        title: newTransmission.title,
        content: newTransmission.content,
        audio_url: newTransmission.audio_url || null,
        video_url: newTransmission.video_url || null,
        vimeo_url: newTransmission.vimeo_url || null,
        youtube_url: newTransmission.youtube_url || null,
        media_type: newTransmission.media_type || null,
        is_published: true
      });
      toast.success('Gem published successfully!');
      setNewTransmission({ title: '', content: '', audio_url: '', video_url: '', vimeo_url: '', youtube_url: '', media_type: null });
      setShowTransmissionForm(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create transmission');
    }
  };

  const handleDeleteTransmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transmission?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/transmission/${id}`);
      toast.success('Gem deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete transmission');
    }
  };

  const handleCreatePrompt = async () => {
    if (!newPrompt.title || !newPrompt.prompt_text) {
      toast.error('Please fill in title and prompt text');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/prompt`, {
        title: newPrompt.title,
        prompt_text: newPrompt.prompt_text,
        is_published: true
      });
      toast.success('Reflection prompt published successfully!');
      setNewPrompt({ title: '', prompt_text: '' });
      setShowPromptForm(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create prompt');
    }
  };

  const handleDeletePrompt = async (id) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/prompt/${id}`);
      toast.success('Prompt deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete prompt');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate days until due (NET 60 from creation)
  const getInvoiceDueInfo = (createdAt) => {
    const created = new Date(createdAt);
    const due = new Date(created.getTime() + (60 * 24 * 60 * 60 * 1000)); // NET 60
    const today = new Date();
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return {
      dueDate: due,
      daysUntilDue: diffDays,
      isOverdue: diffDays < 0
    };
  };

  // Sort invoices based on selected sort option
  const sortInvoices = (invoices) => {
    const statusPriority = { pending: 1, approved: 2, returned: 3, paid: 4, rescinded: 5 };
    
    return [...invoices].sort((a, b) => {
      switch (invoiceSortBy) {
        case 'status':
          return (statusPriority[a.status] || 99) - (statusPriority[b.status] || 99);
        case 'due':
          // Sort by days until due (overdue first, then soonest due)
          const dueA = getInvoiceDueInfo(a.created_at);
          const dueB = getInvoiceDueInfo(b.created_at);
          return dueA.daysUntilDue - dueB.daysUntilDue;
        case 'amount':
          return (b.amount || 0) - (a.amount || 0); // Highest amount first
        case 'date':
        default:
          return new Date(b.created_at) - new Date(a.created_at); // Newest first
      }
    });
  };

  const tierBadgeColors = {
    inner_circle: 'bg-terracotta',
    inner_edge: 'bg-forest',
    costa_rica: 'bg-terracotta',
    coaching_activation: 'bg-forest',
    sdc: 'bg-terracotta'
  };

  // Get page info based on active tab
  const getPageInfo = () => {
    switch (activeTab) {
      case 'dashboard':
        return { title: 'Admin Dashboard', subtitle: 'Overview of platform metrics and activity' };
      case 'analytics':
        return { title: 'Analytics & Reports', subtitle: 'Track traffic, conversions, and engagement from your FB ads' };
      case 'users':
        return { title: 'User Management', subtitle: 'Manage users, tiers, and wallets' };
      case 'coaches':
        return { title: 'Coach Management', subtitle: 'Add, edit, and manage all coaches' };
      case 'group-sessions':
        return { title: 'Group Sessions', subtitle: 'Schedule and manage group coaching sessions' };
      case 'requests':
        return { title: 'Requests', subtitle: 'Handle hot seat and concierge requests' };
      case 'coach-notes':
        return { title: 'Coach Notes', subtitle: 'View confidential notes from all coaches' };
      case 'coach-invoices':
        return { title: 'Invoice Management', subtitle: 'Review and process coach invoices' };
      case 'messages':
        return { title: 'Messages', subtitle: 'Communicate with any user on the platform' };
      case 'support':
        return { title: 'Technical Support', subtitle: 'System updates and support resources' };
      case 'content':
        return { title: 'Content Management', subtitle: 'Manage transmissions, scriptures, and reflections' };
      case 'communities':
        return { title: 'Sister Circle Management', subtitle: 'Manage Sister Circles, images, and moderate conversations' };
      case 'safety':
        return { title: 'Safety & Moderation', subtitle: 'Review reports, flagged content, and manage user safety' };
      case 'esign':
        return { title: 'E-Signatures', subtitle: 'Send and track coaching agreements' };
      case 'collective':
        return { title: 'Collective Applications', subtitle: 'Review Sister Diamonds Collective applications' };
      case 'gift-applications':
        return { title: 'Gift Retreat Applications', subtitle: 'Review and manage gifted retreat experience applications' };
      case 'affiliates':
        return { title: 'Affiliate Management', subtitle: 'Manage affiliates, commissions, and referral tracking' };
      case 'leads':
        return { title: 'Leads Management', subtitle: 'View and manage leads from ebook downloads and funnels' };
      case 'broadcast':
        return { title: 'Broadcast Messages', subtitle: 'Send email or SMS to all users, by tier, or to coaches' };
      case 'communications':
        return { title: 'Communications Log', subtitle: 'Every outbound email and SMS the system has sent — filter, preview, and resend' };
      case 'email-templates':
        return { title: 'Email Templates', subtitle: 'Customize the language and look of system emails sent to sisters and applicants' };
      case 'soul-food':
        return { title: 'Soul Food & Reflections', subtitle: 'AI-generated weekly inspiration for Inner Circle members' };
      default:
        return { title: 'Admin Panel', subtitle: 'Platform administration' };
    }
  };

  const pageInfo = getPageInfo();

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="admin-page">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-forest">{pageInfo.title}</h1>
            <p className="text-gray-600">{pageInfo.subtitle}</p>
          </div>
          <div className="shrink-0 mt-1">
            <FrontendErrorBadge />
          </div>
        </div>

        {/* Dashboard View */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Reset Gift Lead Funnel widget — self-hides via API once campaign ends */}
            <ResetGiftFunnelWidget />

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="card-base">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-forest/10 flex items-center justify-center">
                      <Users className="h-6 w-6 text-forest" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-forest">{adminStats.totalClients}</p>
                      <p className="text-gray-500">Total Clients</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-base">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-terracotta/10 flex items-center justify-center">
                      <Briefcase className="h-6 w-6 text-terracotta" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-forest">{adminStats.totalCoaches}</p>
                      <p className="text-gray-500">Active Coaches</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-base">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Wallet className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-forest">{coachInvoices.filter(i => i.status === 'pending').length}</p>
                      <p className="text-gray-500">Pending Invoices</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Clients by Program */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Clients by Coaching Program
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-4 bg-sand/30 rounded-xl text-center">
                    <p className="text-2xl font-bold text-forest">{adminStats.clientsByProgram.inner_circle || 0}</p>
                    <p className="text-sm text-gray-600">Inner Circle</p>
                  </div>
                  <div className="p-4 bg-sand/30 rounded-xl text-center">
                    <p className="text-2xl font-bold text-forest">{adminStats.clientsByProgram.inner_edge || 0}</p>
                    <p className="text-sm text-gray-600">Inner Edge</p>
                  </div>
                  <div className="p-4 bg-sand/30 rounded-xl text-center">
                    <p className="text-2xl font-bold text-forest">{adminStats.clientsByProgram.coaching_activation || 0}</p>
                    <p className="text-sm text-gray-600">Signature Coaching</p>
                  </div>
                  <div className="p-4 bg-sand/30 rounded-xl text-center">
                    <p className="text-2xl font-bold text-forest">{adminStats.clientsByProgram.sdc || 0}</p>
                    <p className="text-sm text-gray-600">SDC</p>
                  </div>
                  <div className="p-4 bg-sand/30 rounded-xl text-center">
                    <p className="text-2xl font-bold text-forest">{adminStats.clientsByProgram.costa_rica_retreat || 0}</p>
                    <p className="text-sm text-gray-600">Costa Rica Retreat</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Soul Food Pipeline Status */}
            <SoulFoodPipelineStatus />

            {/* Quick Actions */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => navigate('/admin/users')} variant="outline">
                    <Users className="h-4 w-4 mr-2" /> Manage Users
                  </Button>
                  <Button onClick={() => navigate('/admin/coaches')} variant="outline">
                    <Briefcase className="h-4 w-4 mr-2" /> Manage Coaches
                  </Button>
                  <Button onClick={() => navigate('/admin/group-sessions')} variant="outline">
                    <Calendar className="h-4 w-4 mr-2" /> Group Sessions
                  </Button>
                  <Button onClick={() => navigate('/admin/invoices')} variant="outline">
                    <Wallet className="h-4 w-4 mr-2" /> Review Invoices
                  </Button>
                  <Button onClick={() => navigate('/admin/content')} variant="outline">
                    <Radio className="h-4 w-4 mr-2" /> Manage Content
                  </Button>
                  <Button onClick={() => navigate('/admin/collective')} variant="outline">
                    <Sparkles className="h-4 w-4 mr-2" /> Collective Apps
                  </Button>
                  <Button onClick={() => navigate('/admin/leads')} variant="outline" className="text-purple-600 border-purple-300 hover:bg-purple-50">
                    <BookOpen className="h-4 w-4 mr-2" /> Leads
                  </Button>
                  <Button onClick={() => navigate('/admin/messages')} variant="outline">
                    <MessageSquare className="h-4 w-4 mr-2" /> Messages
                  </Button>
                  <Button onClick={() => navigate('/videos')} variant="outline">
                    <Video className="h-4 w-4 mr-2" /> Manage Library
                  </Button>
                  <Button 
                    onClick={handleSyncVipCircles} 
                    variant="outline"
                    disabled={syncingVipCircles}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                    data-testid="sync-vip-circles-btn"
                  >
                    {syncingVipCircles ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Crown className="h-4 w-4 mr-2" />
                    )}
                    {syncingVipCircles ? 'Syncing...' : 'Sync VIP Circles'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Coaches Tab */}
        {activeTab === 'coaches' && <AdminCoachesTab />}
        {/* Group Sessions Tab */}
        {activeTab === 'group-sessions' && <AdminGroupSessionsTab />}

        {/* Technical Support Tab */}
        {activeTab === 'support' && (
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest">Technical Support</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-6 bg-blue-50 rounded-xl">
                <h3 className="font-medium text-forest mb-2">System Updates & Support</h3>
                <p className="text-gray-600 mb-4">Need help with system updates, bug fixes, or new feature requests? Contact Emergent support.</p>
                <Button onClick={() => window.open('https://emergent.sh/support', '_blank')} className="btn-primary">
                  <MessageSquare className="h-4 w-4 mr-2" /> Contact Emergent Support
                </Button>
              </div>
              <div className="p-6 bg-sand/30 rounded-xl">
                <h3 className="font-medium text-forest mb-2">Documentation</h3>
                <p className="text-gray-600 mb-4">Access platform documentation and guides.</p>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => window.open('https://emergent.sh/docs', '_blank')}>
                    View Documentation
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {/* Analytics Tab */}
        {activeTab === 'analytics' && <AdminAnalyticsTab />}
        {/* Users Tab */}
        {activeTab === 'users' && <AdminUsersManagement />}


          {/* Sister Circles Tab */}
          {activeTab === 'communities' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="font-heading text-xl text-forest">Sister Circles</h2>
                <Button 
                  onClick={() => setShowCreateCircleDialog(true)}
                  className="bg-forest hover:bg-forest/90 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" /> Create Circle
                </Button>
              </div>
              
              {/* Circle Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {adminCommunities.map((community) => (
                  <Card key={community.id} className="card-base">
                    <div className="relative h-32 overflow-hidden rounded-t-xl">
                      <img 
                        src={community.image_url || 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400'} 
                        alt={community.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2 flex gap-1">
                        <Badge className="bg-white/90 text-forest text-xs">{community.category}</Badge>
                        {community.is_vip_circle && (
                          <Badge className="bg-purple-600 text-white text-xs">VIP</Badge>
                        )}
                        {community.is_system && (
                          <Badge className="bg-blue-600 text-white text-xs">System</Badge>
                        )}
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-forest mb-1 line-clamp-1">{community.name}</h3>
                      <p className="text-xs text-gray-500 mb-2 line-clamp-2">{community.description}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
                        <span>{community.member_count || 0} members</span>
                        <span>{community.post_count || 0} posts</span>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button 
                          size="sm" 
                          className="w-full bg-forest hover:bg-forest/90 text-white text-xs"
                          onClick={() => {
                            // Navigate to correct route based on community type
                            if (community.id.startsWith('vip-')) {
                              navigate(`/circles/${community.id}`);
                            } else {
                              navigate(`/communities/${community.id}`);
                            }
                          }}
                          data-testid={`enter-circle-${community.id}`}
                        >
                          <ArrowRight className="h-3 w-3 mr-1" /> Enter Circle
                        </Button>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1 text-xs"
                            onClick={() => setEditingCommunity(community)}
                          >
                            <Edit className="h-3 w-3 mr-1" /> Edit
                          </Button>
                          {!community.is_vip_circle && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-xs text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteCircle(community.id, community.name)}
                              title={community.is_system ? "Delete system circle" : "Delete circle"}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1 text-xs"
                            onClick={() => {
                              setSelectedCommunityForPosts(community);
                              fetchCommunityPosts(community.id);
                            }}
                          >
                            <MessageSquare className="h-3 w-3 mr-1" /> Moderate
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Create Circle Dialog */}
              <Dialog open={showCreateCircleDialog} onOpenChange={setShowCreateCircleDialog}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl text-forest">Create New Sister Circle</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Circle Name *</Label>
                      <Input 
                        value={newCircleName}
                        onChange={(e) => setNewCircleName(e.target.value)}
                        placeholder="e.g., Wellness Warriors"
                      />
                    </div>
                    <div>
                      <Label>Description *</Label>
                      <Textarea 
                        value={newCircleDescription}
                        onChange={(e) => setNewCircleDescription(e.target.value)}
                        placeholder="What is this circle about?"
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select value={newCircleCategory} onValueChange={setNewCircleCategory}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Sister Circle">Sister Circle</SelectItem>
                          <SelectItem value="Wellness">Wellness</SelectItem>
                          <SelectItem value="Career">Career & Leadership</SelectItem>
                          <SelectItem value="Relationships">Relationships</SelectItem>
                          <SelectItem value="Spirituality">Spirituality</SelectItem>
                          <SelectItem value="Creativity">Creativity</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Image URL (optional)</Label>
                      <Input 
                        value={newCircleImage}
                        onChange={(e) => setNewCircleImage(e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="circlePrivate"
                        checked={newCirclePrivate}
                        onChange={(e) => setNewCirclePrivate(e.target.checked)}
                        className="rounded"
                      />
                      <Label htmlFor="circlePrivate" className="text-sm">Private circle (members must be invited)</Label>
                    </div>
                    <div className="flex gap-2 justify-end pt-4">
                      <Button variant="outline" onClick={() => setShowCreateCircleDialog(false)}>Cancel</Button>
                      <Button 
                        onClick={handleCreateCircle}
                        disabled={!newCircleName || !newCircleDescription || creatingCircle}
                        className="bg-forest hover:bg-forest/90 text-white"
                      >
                        {creatingCircle ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Create Circle
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Edit Circle Dialog */}
              <Dialog open={!!editingCommunity} onOpenChange={() => setEditingCommunity(null)}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Edit Circle: {editingCommunity?.name}</DialogTitle>
                  </DialogHeader>
                  {editingCommunity && (
                    <div className="space-y-4">
                      <div>
                        <Label>Name</Label>
                        <Input 
                          value={editingCommunity.name}
                          onChange={(e) => setEditingCommunity({...editingCommunity, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea 
                          value={editingCommunity.description}
                          onChange={(e) => setEditingCommunity({...editingCommunity, description: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Image URL</Label>
                        <Input 
                          value={editingCommunity.image_url}
                          onChange={(e) => setEditingCommunity({...editingCommunity, image_url: e.target.value})}
                          placeholder="https://..."
                        />
                        {editingCommunity.image_url && (
                          <img 
                            src={editingCommunity.image_url} 
                            alt="Preview" 
                            className="mt-2 h-24 w-full object-cover rounded-lg"
                          />
                        )}
                      </div>
                      <div>
                        <Label>Guidelines</Label>
                        <Textarea 
                          value={editingCommunity.guidelines || ''}
                          onChange={(e) => setEditingCommunity({...editingCommunity, guidelines: e.target.value})}
                          placeholder="Circle guidelines..."
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={() => setEditingCommunity(null)}>Cancel</Button>
                        <Button 
                          className="btn-primary"
                          onClick={() => handleUpdateCommunity(editingCommunity.id, {
                            name: editingCommunity.name,
                            description: editingCommunity.description,
                            image_url: editingCommunity.image_url,
                            guidelines: editingCommunity.guidelines
                          })}
                        >
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              {/* Moderate Posts Dialog */}
              <Dialog open={!!selectedCommunityForPosts} onOpenChange={() => setSelectedCommunityForPosts(null)}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Moderate: {selectedCommunityForPosts?.name}</DialogTitle>
                    <DialogDescription>Review and manage community posts</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    {communityPosts.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No posts in this community yet.</p>
                    ) : (
                      communityPosts.map((post) => (
                        <div key={post.id} className={`p-4 border rounded-lg ${post.is_flagged ? 'border-red-300 bg-red-50' : 'border-sand'}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="h-8 w-8 rounded-full bg-forest/10 flex items-center justify-center">
                                <span className="text-xs font-semibold text-forest">{post.author_name?.charAt(0)}</span>
                              </div>
                              <div>
                                <p className="text-sm font-semibold">{post.author_name}</p>
                                <p className="text-xs text-gray-400">{new Date(post.created_at).toLocaleString()}</p>
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeletePost(selectedCommunityForPosts.id, post.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <p className="text-sm text-gray-700">{post.content}</p>
                          {post.is_flagged && (
                            <Badge className="mt-2 bg-red-100 text-red-700">Flagged: {post.flag_reason}</Badge>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </DialogContent>
              </Dialog>

              {/* Accountability Circle Moderation */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <AdminCircleModeration />
              </div>
            </div>
          )}

          {/* Content Tab */}
          {activeTab === 'content' && (
            <div className="space-y-6">
              {/* Weekly Soul Food Section */}
              <AdminScriptures />
              
              {/* Weekly Gems and Reflections Grid */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Weekly Gems */}
                <Card className="card-base">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                        <Radio className="h-5 w-5 text-terracotta" />
                        Weekly Gems
                      </CardTitle>
                      <Button 
                        onClick={() => setShowTransmissionForm(!showTransmissionForm)}
                        className="btn-primary h-8 text-xs"
                        data-testid="new-transmission-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" /> New
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* New Gem Form */}
                  {showTransmissionForm && (
                    <div className="p-4 bg-sand/30 rounded-xl mb-4 space-y-3" data-testid="transmission-form">
                      <div>
                        <Label className="text-sm font-medium text-forest">Title</Label>
                        <Input 
                          placeholder="e.g., Embracing Your Power This Week"
                          value={newTransmission.title}
                          onChange={(e) => setNewTransmission(prev => ({ ...prev, title: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-forest">Message Content</Label>
                        <Textarea 
                          placeholder="Write your weekly transmission message to your Inner Circle..."
                          value={newTransmission.content}
                          onChange={(e) => setNewTransmission(prev => ({ ...prev, content: e.target.value }))}
                          className="mt-1 min-h-[120px]"
                        />
                      </div>
                      
                      {/* Media Upload Section */}
                      <div>
                        <Label className="text-sm font-medium text-forest mb-2 block">Attach Media (optional)</Label>
                        
                        {/* Hidden file inputs */}
                        <input
                          type="file"
                          ref={audioInputRef}
                          onChange={(e) => handleMediaUpload(e, 'audio')}
                          accept=".mp3,.wav,.m4a,.aac,.ogg,.flac"
                          className="hidden"
                        />
                        <input
                          type="file"
                          ref={videoInputRef}
                          onChange={(e) => handleMediaUpload(e, 'video')}
                          accept=".mp4,.mov,.webm,.avi,.mkv"
                          className="hidden"
                        />
                        
                        {/* Upload progress */}
                        {uploadingMedia && (
                          <div className="mb-3">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm text-gray-600">Uploading...</span>
                              <span className="text-sm font-medium text-terracotta">{uploadProgress}%</span>
                            </div>
                            <Progress value={uploadProgress} className="h-2" />
                          </div>
                        )}
                        
                        {/* Show uploaded media or upload buttons */}
                        {newTransmission.media_type ? (
                          <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-sand">
                            {newTransmission.media_type === 'audio' && (
                              <>
                                <Music className="h-5 w-5 text-terracotta" />
                                <span className="text-sm text-gray-700 flex-1">Audio attached</span>
                                <audio controls className="h-8 max-w-[200px]">
                                  <source src={`${API_URL}${newTransmission.audio_url}`} />
                                </audio>
                              </>
                            )}
                            {newTransmission.media_type === 'video' && (
                              <>
                                <Video className="h-5 w-5 text-terracotta" />
                                <span className="text-sm text-gray-700 flex-1">Video file attached</span>
                              </>
                            )}
                            {newTransmission.media_type === 'vimeo' && (
                              <>
                                <Link2 className="h-5 w-5 text-terracotta" />
                                <span className="text-sm text-gray-700 flex-1 truncate">Vimeo: {newTransmission.vimeo_url}</span>
                              </>
                            )}
                            {newTransmission.media_type === 'youtube' && (
                              <>
                                <Video className="h-5 w-5 text-red-500" />
                                <span className="text-sm text-gray-700 flex-1 truncate">YouTube: {newTransmission.youtube_url}</span>
                              </>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={clearMedia}
                              className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {/* Upload buttons */}
                            <div className="flex gap-2 flex-wrap">
                              <Button 
                                type="button"
                                variant="outline" 
                                size="sm"
                                onClick={() => audioInputRef.current?.click()}
                                disabled={uploadingMedia}
                                className="text-sm"
                              >
                                <Music className="h-4 w-4 mr-2" /> Upload Audio
                              </Button>
                              <Button 
                                type="button"
                                variant="outline" 
                                size="sm"
                                onClick={() => videoInputRef.current?.click()}
                                disabled={uploadingMedia}
                                className="text-sm"
                              >
                                <Video className="h-4 w-4 mr-2" /> Upload Video
                              </Button>
                            </div>
                            
                            {/* Vimeo URL input */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">or</span>
                              <div className="flex-1 relative">
                                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input 
                                  placeholder="Paste Vimeo URL (e.g., https://vimeo.com/123456789)"
                                  value={newTransmission.vimeo_url}
                                  onChange={(e) => handleVimeoUrlChange(e.target.value)}
                                  className="pl-9 text-sm"
                                />
                              </div>
                            </div>
                            
                            {/* YouTube URL input */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">or</span>
                              <div className="flex-1 relative">
                                <Video className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
                                <Input 
                                  placeholder="Paste YouTube URL (e.g., https://youtube.com/watch?v=...)"
                                  value={newTransmission.youtube_url}
                                  onChange={(e) => handleYoutubeUrlChange(e.target.value)}
                                  className="pl-9 text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          Upload: Audio (MP3, WAV) or Video (MP4, MOV) up to 100MB • Or paste a Vimeo/YouTube URL
                        </p>
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        <Button onClick={handleCreateTransmission} className="btn-secondary text-sm" data-testid="publish-transmission-btn" disabled={uploadingMedia}>
                          <Send className="h-4 w-4 mr-2" /> Publish
                        </Button>
                        <Button variant="outline" onClick={() => { setShowTransmissionForm(false); clearMedia(); }} className="text-sm">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Gems List */}
                  {transmissions.length === 0 ? (
                    <p className="text-gray-500 text-center py-6">No transmissions yet. Create your first one!</p>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {transmissions.map((t) => (
                        <div key={t.id} className="p-4 border border-sand rounded-xl hover:border-terracotta/30 transition-colors" data-testid={`transmission-${t.id}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h4 className="font-medium text-forest">{t.title}</h4>
                              <p className="text-xs text-gray-400 mt-0.5">{formatDate(t.created_at)}</p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeleteTransmission(t.id)}
                              className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-3">{t.content}</p>
                          {t.media_type === 'audio' && t.audio_url && (
                            <div className="mt-2 flex items-center gap-2">
                              <Music className="h-4 w-4 text-terracotta" />
                              <span className="text-xs text-terracotta">Audio attached</span>
                            </div>
                          )}
                          {t.media_type === 'video' && t.video_url && (
                            <div className="mt-2 flex items-center gap-2">
                              <Video className="h-4 w-4 text-terracotta" />
                              <span className="text-xs text-terracotta">Video attached</span>
                            </div>
                          )}
                          {t.media_type === 'vimeo' && t.vimeo_url && (
                            <div className="mt-2 flex items-center gap-2">
                              <Link2 className="h-4 w-4 text-terracotta" />
                              <span className="text-xs text-terracotta">Vimeo video</span>
                            </div>
                          )}
                          {t.media_type === 'youtube' && t.youtube_url && (
                            <div className="mt-2 flex items-center gap-2">
                              <Video className="h-4 w-4 text-red-500" />
                              <span className="text-xs text-red-500">YouTube video</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Weekly Reflection Prompts */}
              <Card className="card-base">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                      <FileText className="h-5 w-5 text-terracotta" />
                      Reflection Prompts
                    </CardTitle>
                    <Button 
                      onClick={() => setShowPromptForm(!showPromptForm)}
                      className="btn-primary h-8 text-xs"
                      data-testid="new-prompt-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" /> New
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* New Prompt Form */}
                  {showPromptForm && (
                    <div className="p-4 bg-sand/30 rounded-xl mb-4 space-y-3" data-testid="prompt-form">
                      <div>
                        <Label className="text-sm font-medium text-forest">Title</Label>
                        <Input 
                          placeholder="e.g., Weekly Reflection: Boundaries"
                          value={newPrompt.title}
                          onChange={(e) => setNewPrompt(prev => ({ ...prev, title: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-forest">Prompt Question</Label>
                        <Textarea 
                          placeholder="Write a reflective question for your community..."
                          value={newPrompt.prompt_text}
                          onChange={(e) => setNewPrompt(prev => ({ ...prev, prompt_text: e.target.value }))}
                          className="mt-1 min-h-[100px]"
                        />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button onClick={handleCreatePrompt} className="btn-secondary text-sm" data-testid="publish-prompt-btn">
                          <Send className="h-4 w-4 mr-2" /> Publish
                        </Button>
                        <Button variant="outline" onClick={() => setShowPromptForm(false)} className="text-sm">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Prompts List */}
                  {prompts.length === 0 ? (
                    <p className="text-gray-500 text-center py-6">No prompts yet. Create your first one!</p>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {prompts.map((p) => (
                        <div key={p.id} className="p-4 border border-sand rounded-xl hover:border-terracotta/30 transition-colors" data-testid={`prompt-${p.id}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h4 className="font-medium text-forest">{p.title}</h4>
                              <p className="text-xs text-gray-400 mt-0.5">{formatDate(p.created_at)}</p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeletePrompt(p.id)}
                              className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <p className="text-sm text-gray-600 italic">&ldquo;{p.prompt_text}&rdquo;</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              </div>

              {/* Library Management Section */}
              <AdminLibraryTab />
            </div>
          )}

          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest">Session Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No bookings yet.</p>
                ) : (
                  <div className="space-y-4">
                    {bookings.map((booking) => (
                      <div key={booking.id} className="flex items-center justify-between p-4 bg-sand/30 rounded-xl">
                        <div>
                          <p className="font-medium text-forest">
                            {booking.coach_name} - {booking.session_type === 'wonder_twin' ? 'Wonder Twin' : 'Coach Session'}
                          </p>
                          <p className="text-sm text-gray-500">User ID: {booking.user_id.slice(0, 8)}...</p>
                          <p className="text-xs text-gray-400">{new Date(booking.created_at).toLocaleString()}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={
                            booking.status === 'completed' ? 'bg-green-100 text-green-700' :
                            booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {booking.status}
                          </Badge>
                          {booking.status === 'pending' && (
                            <Button 
                              size="sm" 
                              onClick={() => handleCompleteSession(booking.id)}
                              className="btn-primary text-xs"
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Requests Tab */}
          {activeTab === 'requests' && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Hot Seat Requests */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Flame className="h-5 w-5 text-terracotta" />
                    Hot Seat Requests
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {hotSeats.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No hot seat requests.</p>
                  ) : (
                    <div className="space-y-4">
                      {hotSeats.map((req) => (
                        <div key={req.id} className="p-4 bg-sand/30 rounded-xl">
                          <div className="flex justify-between mb-2">
                            <p className="font-medium text-forest">{req.topic}</p>
                            <Badge className={
                              req.status === 'approved' ? 'bg-green-100 text-green-700' :
                              'bg-yellow-100 text-yellow-700'
                            }>
                              {req.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{req.description}</p>
                          <p className="text-xs text-gray-400">From: {req.user_name}</p>
                          {req.status === 'pending' && (
                            <div className="flex gap-2 mt-3">
                              <Button size="sm" onClick={() => handleUpdateRequest('hot-seat', req.id, 'approved')} className="btn-primary text-xs">
                                Approve
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleUpdateRequest('hot-seat', req.id, 'declined')} className="text-xs">
                                Decline
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Concierge Requests */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-terracotta" />
                    Concierge Requests
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {concierge.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No concierge requests.</p>
                  ) : (
                    <div className="space-y-4">
                      {concierge.map((req) => (
                        <div key={req.id} className="p-4 bg-sand/30 rounded-xl">
                          <div className="flex justify-between mb-2">
                            <p className="font-medium text-forest capitalize">{req.request_type}</p>
                            <Badge className={
                              req.status === 'completed' ? 'bg-green-100 text-green-700' :
                              'bg-yellow-100 text-yellow-700'
                            }>
                              {req.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{req.description}</p>
                          <p className="text-xs text-gray-400">From: {req.user_name} | Urgency: {req.urgency}</p>
                          {req.status === 'pending' && (
                            <div className="flex gap-2 mt-3">
                              <Button size="sm" onClick={() => handleUpdateRequest('concierge', req.id, 'completed')} className="btn-primary text-xs">
                                Complete
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Coach Notes Tab */}
          {activeTab === 'coach-notes' && (
            <Card className="card-base">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Coach Confidential Notes
                    </CardTitle>
                    <p className="text-sm text-gray-500">View all confidential notes from coaches about their clients</p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="text-sm text-gray-500">Sort by:</span>
                    <Select value={notesSortBy} onValueChange={setNotesSortBy}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date">Date</SelectItem>
                        <SelectItem value="coach">Coach Name</SelectItem>
                        <SelectItem value="client">Client Name</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Coaching Notes Guidance */}
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
                  <h4 className="font-semibold text-amber-800 mb-2">Coaching Notes Guidance</h4>
                  <p className="text-amber-700 mb-2">
                    Notes are for brief, high-level documentation only—session verification, invoicing, and general progress tracking.
                  </p>
                  <p className="text-amber-700 font-medium mb-1">Notes should include:</p>
                  <ul className="text-amber-700 list-disc list-inside mb-2 grid grid-cols-2 sm:grid-cols-5 gap-1">
                    <li>Date</li>
                    <li>Duration</li>
                    <li>General focus</li>
                    <li>Action steps</li>
                    <li>Next session</li>
                  </ul>
                  <p className="text-amber-800 font-medium">
                    Notes should NOT include sensitive personal, medical, trauma, legal, or diagnostic information.
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                {/* Search and Filter Bar */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="grid sm:grid-cols-3 gap-4">
                    {/* Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search notes..."
                        value={notesSearchTerm}
                        onChange={(e) => setNotesSearchTerm(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    
                    {/* Coach Filter */}
                    <Select value={notesCoachFilter} onValueChange={setNotesCoachFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by Coach" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Coaches</SelectItem>
                        {[...new Set(coachNotes.map(n => n.coach_name).filter(Boolean))].map(coach => (
                          <SelectItem key={coach} value={coach}>{coach}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Client Filter */}
                    <Select value={notesClientFilter} onValueChange={setNotesClientFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by Client" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Clients</SelectItem>
                        {[...new Set(coachNotes.map(n => n.client_name).filter(Boolean))].map(client => (
                          <SelectItem key={client} value={client}>{client}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Clear Filters */}
                  {(notesSearchTerm || (notesCoachFilter && notesCoachFilter !== 'all') || (notesClientFilter && notesClientFilter !== 'all')) && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="mt-2 text-terracotta"
                      onClick={() => {
                        setNotesSearchTerm('');
                        setNotesCoachFilter('');
                        setNotesClientFilter('');
                      }}
                    >
                      Clear all filters
                    </Button>
                  )}
                </div>

                {coachNotes.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No coach notes found.</p>
                ) : (
                  <div className="space-y-4">
                    {[...coachNotes]
                      .filter(note => {
                        // Search filter
                        if (notesSearchTerm) {
                          const search = notesSearchTerm.toLowerCase();
                          const matchesSearch = 
                            note.note?.toLowerCase().includes(search) ||
                            note.client_name?.toLowerCase().includes(search) ||
                            note.coach_name?.toLowerCase().includes(search) ||
                            note.client_email?.toLowerCase().includes(search);
                          if (!matchesSearch) return false;
                        }
                        // Coach filter
                        if (notesCoachFilter && notesCoachFilter !== 'all' && note.coach_name !== notesCoachFilter) {
                          return false;
                        }
                        // Client filter
                        if (notesClientFilter && notesClientFilter !== 'all' && note.client_name !== notesClientFilter) {
                          return false;
                        }
                        return true;
                      })
                      .sort((a, b) => {
                        if (notesSortBy === 'coach') return (a.coach_name || '').localeCompare(b.coach_name || '');
                        if (notesSortBy === 'client') return (a.client_name || '').localeCompare(b.client_name || '');
                        return new Date(b.created_at) - new Date(a.created_at);
                      })
                      .map((note) => (
                      <div key={note.id} className="p-4 border border-sand rounded-xl">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium text-forest">Re: {note.client_name}</p>
                            <p className="text-xs text-gray-500">{note.client_email}</p>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline" className="text-xs mb-1">{note.note_type}</Badge>
                            <p className="text-xs text-gray-400">{formatDate(note.created_at)}</p>
                          </div>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap mb-3 bg-gray-50 p-3 rounded-lg">{note.note}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="font-medium">Coach:</span>
                          <span>{note.coach_name}</span>
                          <span className="text-gray-300">|</span>
                          <span>{note.coach_email}</span>
                        </div>
                      </div>
                    ))}
                    
                    {/* No results message */}
                    {coachNotes.length > 0 && [...coachNotes].filter(note => {
                      if (notesSearchTerm) {
                        const search = notesSearchTerm.toLowerCase();
                        const matchesSearch = 
                          note.note?.toLowerCase().includes(search) ||
                          note.client_name?.toLowerCase().includes(search) ||
                          note.coach_name?.toLowerCase().includes(search);
                        if (!matchesSearch) return false;
                      }
                      if (notesCoachFilter && notesCoachFilter !== 'all' && note.coach_name !== notesCoachFilter) return false;
                      if (notesClientFilter && notesClientFilter !== 'all' && note.client_name !== notesClientFilter) return false;
                      return true;
                    }).length === 0 && (
                      <p className="text-gray-500 text-center py-8">No notes match your filters.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Coach Invoices Tab */}
          {activeTab === 'coach-invoices' && (
            <Card className="card-base">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                      <Wallet className="h-5 w-5" />
                      Coach Invoices
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">Review and process coach invoices for payment</p>
                  </div>
                  <div className="flex gap-3 items-center">
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-500">Filter:</span>
                      <Select value={invoiceStatusFilter || "all"} onValueChange={(v) => setInvoiceStatusFilter(v === "all" ? "" : v)}>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="returned">Returned</SelectItem>
                          <SelectItem value="rescinded">Rescinded</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-500">Sort:</span>
                      <Select value={invoiceSortBy} onValueChange={setInvoiceSortBy}>
                        <SelectTrigger className="w-36">
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="date">Newest First</SelectItem>
                          <SelectItem value="status">By Status</SelectItem>
                          <SelectItem value="due">Due Soonest</SelectItem>
                          <SelectItem value="amount">Highest Amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {coachInvoices.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No invoices found.</p>
                ) : (
                  <div className="space-y-4">
                    {sortInvoices(coachInvoices).map((invoice) => {
                      const dueInfo = getInvoiceDueInfo(invoice.created_at);
                      const showDueWarning = ['pending', 'approved'].includes(invoice.status);
                      
                      return (
                      <div 
                        key={invoice.id} 
                        className={`p-4 border rounded-xl ${
                          showDueWarning && dueInfo.isOverdue 
                            ? 'border-red-300 bg-red-50/50' 
                            : showDueWarning && dueInfo.daysUntilDue <= 7 
                              ? 'border-orange-300 bg-orange-50/50' 
                              : 'border-sand'
                        }`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <p className="font-semibold text-forest">Invoice #{invoice.invoice_number}</p>
                              <Badge className={
                                invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                                invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                invoice.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                                invoice.status === 'returned' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }>
                                {invoice.status}
                              </Badge>
                              {/* Days Until Due Badge */}
                              {showDueWarning && (
                                <Badge className={
                                  dueInfo.isOverdue 
                                    ? 'bg-red-500 text-white' 
                                    : dueInfo.daysUntilDue <= 7 
                                      ? 'bg-orange-500 text-white' 
                                      : dueInfo.daysUntilDue <= 30 
                                        ? 'bg-yellow-500 text-white' 
                                        : 'bg-gray-200 text-gray-700'
                                }>
                                  {dueInfo.isOverdue 
                                    ? `OVERDUE by ${Math.abs(dueInfo.daysUntilDue)} days` 
                                    : `${dueInfo.daysUntilDue} days until due`}
                                </Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                              <div>
                                <p className="text-gray-500">Coach:</p>
                                <p className="font-medium">{invoice.coach_name}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Client:</p>
                                <p className="font-medium">{invoice.client_name}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Sessions:</p>
                                <p className="font-medium">{invoice.sessions_count}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Dates:</p>
                                <p className="font-medium">{invoice.session_dates?.join(', ') || 'N/A'}</p>
                              </div>
                            </div>
                            {invoice.notes && (
                              <p className="text-sm text-gray-600 mb-2"><strong>Notes:</strong> {invoice.notes}</p>
                            )}
                            {invoice.attachment_url && (
                              <a 
                                href={`${API_URL}${invoice.attachment_url}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                              >
                                <Link2 className="h-3 w-3" /> View Attachment
                              </a>
                            )}
                            {invoice.invoice_link && (
                              <a 
                                href={invoice.invoice_link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-green-700 hover:underline ml-3"
                                data-testid={`admin-invoice-pay-link-${invoice.id}`}
                              >
                                <Link2 className="h-3 w-3" /> Invoice Payment Link
                              </a>
                            )}
                            {invoice.admin_comments && (
                              <p className="text-sm text-orange-600 mt-2"><strong>Admin Comments:</strong> {invoice.admin_comments}</p>
                            )}
                            {invoice.payment_details && (
                              <p className="text-sm text-green-600 mt-1"><strong>Payment:</strong> {invoice.payment_details}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-forest">${invoice.amount}</p>
                            <p className="text-xs text-gray-500">Created: {formatDate(invoice.created_at)}</p>
                            {showDueWarning && (
                              <p className={`text-xs mb-3 ${dueInfo.isOverdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                Due: {dueInfo.dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            )}
                            {!showDueWarning && <div className="mb-3" />}
                            
                            {/* Action Buttons */}
                            {invoice.status === 'pending' && (
                              <div className="flex flex-col gap-2">
                                <Button 
                                  size="sm" 
                                  className="btn-primary text-xs"
                                  onClick={() => handleUpdateInvoice(invoice.id, { status: 'approved' })}
                                >
                                  <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-xs text-red-600 border-red-300"
                                  onClick={() => {
                                    const comment = prompt('Enter reason for return:');
                                    if (comment) handleUpdateInvoice(invoice.id, { status: 'returned', admin_comments: comment });
                                  }}
                                >
                                  <XCircle className="h-3 w-3 mr-1" /> Return
                                </Button>
                              </div>
                            )}
                            {invoice.status === 'approved' && (
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                onClick={() => {
                                  const payment = prompt('Enter payment details (e.g., Check #, Date):');
                                  if (payment) handleUpdateInvoice(invoice.id, { status: 'paid', payment_details: payment });
                                }}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Mark Paid
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-xs text-red-600 border-red-300 hover:bg-red-50 mt-1"
                              onClick={() => handleDeleteInvoice(invoice.id, invoice.invoice_number)}
                              data-testid={`delete-invoice-${invoice.id}`}
                            >
                              <Trash2 className="h-3 w-3 mr-1" /> Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="flex h-[calc(100vh-16rem)] border border-sand rounded-lg overflow-hidden bg-white">
              {/* Conversations List */}
              <div className={`w-full md:w-80 border-r border-sand flex flex-col ${selectedPartner ? 'hidden md:flex' : 'flex'}`}>
                <div className="p-4 border-b border-sand">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="font-heading text-xl font-semibold text-forest">Messages</h2>
                  </div>
                  {/* New Message Button */}
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="btn-primary w-full" size="sm">
                        <Plus className="h-4 w-4 mr-2" /> New Message
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="font-heading">Start New Conversation</DialogTitle>
                        <DialogDescription>Select any user to message</DialogDescription>
                      </DialogHeader>
                      <div className="py-4">
                        <Input 
                          placeholder="Search users..." 
                          className="mb-4"
                          onChange={(e) => {
                            const search = e.target.value.toLowerCase();
                            // Filter is applied in the map below
                          }}
                        />
                        <div className="max-h-60 overflow-y-auto space-y-2">
                          {allUsers.filter(u => u.id !== user?.id).map((u) => (
                            <Button
                              key={u.id}
                              variant="outline"
                              className="w-full justify-start"
                              onClick={() => {
                                setSelectedPartner(u);
                                fetchMessagesWithUser(u.id);
                              }}
                            >
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-3 ${
                                u.is_coach ? 'bg-terracotta/10' : 'bg-forest/10'
                              }`}>
                                <User className={`h-4 w-4 ${u.is_coach ? 'text-terracotta' : 'text-forest'}`} />
                              </div>
                              <div className="text-left">
                                <p className="font-medium">{u.full_name}</p>
                                <p className="text-xs text-gray-500">{u.email} {u.is_coach && '(Coach)'}</p>
                              </div>
                            </Button>
                          ))}
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                  {conversations.length === 0 ? (
                    <div className="p-8 text-center">
                      <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No conversations yet</p>
                      <p className="text-sm text-gray-400 mt-2">Click "New Message" to start</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-sand">
                      {conversations.map((conv) => (
                        <button 
                          key={conv.partner?.id}
                          onClick={() => {
                            setSelectedPartner(conv.partner);
                            fetchMessagesWithUser(conv.partner?.id);
                          }}
                          className={`w-full flex items-center gap-3 p-4 hover:bg-sand/50 transition-colors text-left ${
                            selectedPartner?.id === conv.partner?.id ? 'bg-sand' : ''
                          }`}
                        >
                          <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                            conv.partner?.type === 'coach' ? 'bg-terracotta/10' : 'bg-forest/10'
                          }`}>
                            <User className={`h-6 w-6 ${conv.partner?.type === 'coach' ? 'text-terracotta' : 'text-forest'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h3 className="font-medium text-forest truncate">{conv.partner?.full_name}</h3>
                              {conv.last_message && (
                                <span className="text-xs text-gray-400">
                                  {formatMessageTime(conv.last_message.created_at)}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={`text-xs ${
                                conv.partner?.type === 'coach' ? 'border-terracotta/50 text-terracotta' : ''
                              }`}>
                                {conv.partner?.type === 'coach' ? 'Coach' : 'Client'}
                              </Badge>
                              {conv.last_message && (
                                <p className="text-sm text-gray-500 truncate flex-1">{conv.last_message.content}</p>
                              )}
                            </div>
                          </div>
                          {conv.unread_count > 0 && (
                            <span className="h-5 w-5 rounded-full bg-terracotta text-white text-xs flex items-center justify-center">
                              {conv.unread_count}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Chat Area */}
              <div className={`flex-1 flex flex-col bg-cream/50 ${!selectedPartner ? 'hidden md:flex' : 'flex'}`}>
                {selectedPartner ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 border-b border-sand bg-white flex items-center gap-3">
                      <button onClick={() => setSelectedPartner(null)} className="md:hidden">
                        <XCircle className="h-5 w-5" />
                      </button>
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                        selectedPartner.type === 'coach' || selectedPartner.is_coach ? 'bg-terracotta/10' : 'bg-forest/10'
                      }`}>
                        <User className={`h-5 w-5 ${selectedPartner.type === 'coach' || selectedPartner.is_coach ? 'text-terracotta' : 'text-forest'}`} />
                      </div>
                      <div>
                        <h2 className="font-medium text-forest">{selectedPartner.full_name}</h2>
                        <p className="text-sm text-gray-500">{selectedPartner.email}</p>
                      </div>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4">
                      <div className="space-y-4 max-w-3xl mx-auto">
                        {messages.length === 0 ? (
                          <div className="text-center py-8">
                            <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">No messages yet</p>
                            <p className="text-sm text-gray-400">Send a message to start the conversation</p>
                          </div>
                        ) : (
                          messages.map((msg) => {
                            const isSent = msg.sender_id === user?.id;
                            return (
                              <div key={msg.id} className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[70%] rounded-xl px-4 py-2 ${
                                  isSent 
                                    ? 'bg-forest text-white rounded-br-sm' 
                                    : 'bg-white border border-sand text-gray-800 rounded-bl-sm'
                                }`}>
                                  <p>{msg.content}</p>
                                  <span className={`text-xs mt-1 block ${isSent ? 'text-white/70' : 'text-gray-500'}`}>
                                    {formatMessageTime(msg.created_at)}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    </div>

                    {/* Message Input */}
                    <div className="p-4 border-t border-sand bg-white">
                      <form onSubmit={handleSendAdminMessage} className="flex gap-3 max-w-3xl mx-auto">
                        <Input
                          placeholder="Type a message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          className="input-base flex-1"
                        />
                        <Button 
                          type="submit" 
                          className="btn-primary"
                          disabled={!newMessage.trim() || sendingMessage}
                        >
                          <Send className="h-5 w-5" />
                        </Button>
                      </form>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="font-heading font-semibold text-forest mb-2">Select a Conversation</h3>
                      <p className="text-gray-500">Choose a user to start messaging</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Safety & Moderation Tab */}
          {activeTab === 'safety' && (
            <AdminSafetyDashboard />
          )}

          {/* E-Signatures Tab */}
          {activeTab === 'esign' && (
            <AdminESignatures />
          )}

          {/* Costa Rica Retreat Tab */}
          {activeTab === 'retreat' && (
            <AdminRetreatTab />
          )}

          {/* Legacy Circle Tab */}
          {activeTab === 'legacy' && (
            <AdminLegacyTab />
          )}

          {activeTab === 'billing' && <AdminBillingTab />}

          {/* Broadcast Messages Tab */}
          {activeTab === 'broadcast' && (
            <AdminBroadcastTab />
          )}

          {/* Communications Log Tab */}
          {activeTab === 'communications' && (
            <AdminCommunicationsLog />
          )}

          {/* Email Templates Tab */}
          {activeTab === 'email-templates' && (
            <AdminEmailTemplates />
          )}

          {/* Soul Food & Reflections Tab */}
          {activeTab === 'soul-food' && (
            <AdminSoulFoodTab />
          )}

          {/* Leads Management Tab */}
          {activeTab === 'leads' && (
            <Tabs defaultValue="funnel" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="funnel" data-testid="leads-funnel-tab">Funnel Analytics</TabsTrigger>
                <TabsTrigger value="all-leads" data-testid="leads-all-tab">All Leads</TabsTrigger>
              </TabsList>
              <TabsContent value="funnel">
                <AdminFunnelDashboard />
              </TabsContent>
              <TabsContent value="all-leads">
                <AdminLeadsManagement />
              </TabsContent>
            </Tabs>
          )}

          {/* Collective Applications Tab */}
          {activeTab === 'collective' && (
            <Card className="card-base">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-600" />
                    Collective Applications 
                    {collectiveFilter && collectiveFilter !== 'total' 
                      ? <span className="text-base font-normal text-purple-600">({filteredCollectiveApplications.length} of {collectiveApplications.length})</span>
                      : <span className="text-base font-normal text-gray-500">({collectiveApplications.length})</span>
                    }
                  </CardTitle>
                  <Button variant="outline" onClick={fetchCollectiveApplications} disabled={loadingCollective}>
                    {loadingCollective ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Stats - Clickable Filter Cards */}
                {collectiveStats && Object.keys(collectiveStats).length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <div 
                      className={`p-4 bg-purple-50 rounded-xl text-center cursor-pointer transition-all hover:shadow-md ${
                        collectiveFilter === 'total' ? 'ring-2 ring-purple-500 shadow-md' : ''
                      }`}
                      onClick={() => setCollectiveFilter(collectiveFilter === 'total' ? null : 'total')}
                      data-testid="filter-total"
                    >
                      <p className="text-2xl font-bold text-purple-600">{collectiveStats.total || 0}</p>
                      <p className="text-sm text-gray-600">Total</p>
                    </div>
                    <div 
                      className={`p-4 bg-yellow-50 rounded-xl text-center cursor-pointer transition-all hover:shadow-md ${
                        collectiveFilter === 'new' ? 'ring-2 ring-yellow-500 shadow-md' : ''
                      }`}
                      onClick={() => setCollectiveFilter(collectiveFilter === 'new' ? null : 'new')}
                      data-testid="filter-new"
                    >
                      <p className="text-2xl font-bold text-yellow-600">{collectiveStats.new || 0}</p>
                      <p className="text-sm text-gray-600">New</p>
                    </div>
                    <div 
                      className={`p-4 bg-orange-50 rounded-xl text-center cursor-pointer transition-all hover:shadow-md ${
                        collectiveFilter === 'inner_edge' ? 'ring-2 ring-orange-500 shadow-md' : ''
                      }`}
                      onClick={() => setCollectiveFilter(collectiveFilter === 'inner_edge' ? null : 'inner_edge')}
                      data-testid="filter-inner-edge"
                    >
                      <p className="text-2xl font-bold text-orange-600">{collectiveStats.inner_edge || 0}</p>
                      <p className="text-sm text-gray-600">Inner Edge Path</p>
                    </div>
                    <div 
                      className={`p-4 bg-green-50 rounded-xl text-center cursor-pointer transition-all hover:shadow-md ${
                        collectiveFilter === 'inner_circle' ? 'ring-2 ring-green-500 shadow-md' : ''
                      }`}
                      onClick={() => setCollectiveFilter(collectiveFilter === 'inner_circle' ? null : 'inner_circle')}
                      data-testid="filter-inner-circle"
                    >
                      <p className="text-2xl font-bold text-green-600">{collectiveStats.inner_circle || 0}</p>
                      <p className="text-sm text-gray-600">Inner Circle Path</p>
                    </div>
                    <div 
                      className={`p-4 bg-emerald-50 rounded-xl text-center cursor-pointer transition-all hover:shadow-md ${
                        collectiveFilter === 'converted' ? 'ring-2 ring-emerald-500 shadow-md' : ''
                      }`}
                      onClick={() => setCollectiveFilter(collectiveFilter === 'converted' ? null : 'converted')}
                      data-testid="filter-converted"
                    >
                      <p className="text-2xl font-bold text-emerald-600">{collectiveStats.converted || 0}</p>
                      <p className="text-sm text-gray-600">Converted</p>
                    </div>
                  </div>
                )}

                {/* Active Filter Indicator */}
                {collectiveFilter && (
                  <div className="mb-4 flex items-center justify-between p-3 bg-purple-100 rounded-lg">
                    <span className="text-sm text-purple-800 font-medium">
                      Showing: {collectiveFilter === 'total' ? 'All Applications' : 
                        collectiveFilter === 'new' ? 'New Applications' : 
                        collectiveFilter === 'inner_edge' ? 'Inner Edge Path' :
                        collectiveFilter === 'inner_circle' ? 'Inner Circle Path' :
                        collectiveFilter === 'converted' ? 'Converted Users' : collectiveFilter}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setCollectiveFilter(null)}
                      className="text-purple-700 hover:text-purple-900 hover:bg-purple-200"
                      data-testid="clear-filter-btn"
                    >
                      <X className="h-4 w-4 mr-1" /> Clear Filter
                    </Button>
                  </div>
                )}

                {loadingCollective ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-600" />
                    <p className="text-gray-500 mt-2">Loading applications...</p>
                  </div>
                ) : collectiveApplications.length === 0 ? (
                  <div className="text-center py-12 bg-purple-50/50 rounded-xl">
                    <Sparkles className="h-12 w-12 text-purple-300 mx-auto mb-3" />
                    <p className="text-gray-500">No applications yet.</p>
                    <p className="text-sm text-gray-400 mt-1">Applications from /apply-collective will appear here.</p>
                  </div>
                ) : filteredCollectiveApplications.length === 0 ? (
                  <div className="text-center py-12 bg-purple-50/50 rounded-xl">
                    <Sparkles className="h-12 w-12 text-purple-300 mx-auto mb-3" />
                    <p className="text-gray-500">No applications match this filter.</p>
                    <Button 
                      variant="link" 
                      onClick={() => setCollectiveFilter(null)}
                      className="text-purple-600 mt-2"
                    >
                      Clear filter to see all applications
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredCollectiveApplications.map((app) => (
                      <div key={app.id} className={`p-4 border rounded-xl hover:border-purple-300 transition-colors ${
                        app.purchase_status?.has_inner_circle || app.purchase_status?.has_inner_edge 
                          ? 'border-emerald-300 bg-emerald-50/30' 
                          : 'border-sand'
                      }`}>
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center flex-wrap gap-2 mb-2">
                              <h3 className="font-semibold text-forest">{app.contact?.name || 'Unknown'}</h3>
                              <Badge className={
                                app.status === 'accepted' ? 'bg-green-100 text-green-700' :
                                app.status === 'interviewed' ? 'bg-blue-100 text-blue-700' :
                                app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }>
                                {app.status || 'new'}
                              </Badge>
                              <Badge variant="outline" className="text-purple-600 border-purple-300">
                                {app.outcome === 'collective' ? 'Collective' :
                                 app.outcome === 'inner_edge' ? 'Inner Edge' :
                                 app.outcome === 'inner_circle' ? 'Inner Circle' : app.outcome}
                              </Badge>
                              
                              {/* Purchase Status Badges */}
                              {app.purchase_status?.has_inner_circle && (
                                <Badge className="bg-emerald-500 text-white">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Bought Inner Circle
                                </Badge>
                              )}
                              {app.purchase_status?.has_inner_edge && (
                                <Badge className="bg-orange-500 text-white">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Bought Inner Edge
                                </Badge>
                              )}
                              {app.purchase_status?.has_sdc && (
                                <Badge className="bg-amber-500 text-white">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Bought SDC
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                              <span className="flex items-center gap-1">
                                <Mail className="h-4 w-4" /> {app.contact?.email}
                              </span>
                              {app.contact?.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-4 w-4" /> {app.contact?.phone}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {new Date(app.completed_at || app.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            
                            {/* Expandable answers */}
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="outline" size="sm" onClick={() => setSelectedApplication(app)}>
                                  <FileText className="h-4 w-4 mr-1" /> View Full Application
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle>Application from {app.contact?.name}</DialogTitle>
                                  <DialogDescription>
                                    Submitted on {new Date(app.completed_at || app.created_at).toLocaleString()}
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  {/* Outcome Explanation Section */}
                                  {app.outcome_explanation && (
                                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4 mb-6">
                                      <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                                        <Sparkles className="h-4 w-4" />
                                        Routing Explanation: {app.outcome === 'collective' ? 'Collective Interview' : 
                                          app.outcome === 'inner_edge' ? 'Inner Edge' : 
                                          app.outcome === 'inner_circle' ? 'Inner Circle' : app.outcome}
                                      </h4>
                                      <p className="text-purple-700 text-sm mb-3">{app.outcome_explanation.summary}</p>
                                      {app.outcome_explanation.reasons && app.outcome_explanation.reasons.length > 0 && (
                                        <div className="space-y-2">
                                          {app.outcome_explanation.reasons.map((reason, idx) => (
                                            <div key={idx} className="bg-white/60 rounded-lg p-2 text-sm">
                                              <span className="font-medium text-purple-800">{reason.question}:</span>
                                              <span className="text-gray-700 ml-1">{reason.answer}</span>
                                              <p className="text-gray-500 text-xs mt-1 italic">{reason.reason}</p>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* All Application Responses */}
                                  <h4 className="font-semibold text-forest border-b pb-2">All Responses</h4>
                                  {app.formatted_answers ? (
                                    app.formatted_answers.map((item, idx) => (
                                      <div key={idx} className="border-b border-sand pb-3">
                                        <p className="text-sm font-medium text-gray-700 mb-1">
                                          {item.question}
                                        </p>
                                        <p className="text-gray-600">
                                          {Array.isArray(item.answer_display) 
                                            ? item.answer_display.join(', ') 
                                            : item.answer_display}
                                        </p>
                                      </div>
                                    ))
                                  ) : (
                                    // Fallback for legacy data
                                    app.answers && Object.entries(app.answers).map(([key, value]) => (
                                      <div key={key} className="border-b border-sand pb-3">
                                        <p className="text-sm font-medium text-gray-700 mb-1">
                                          {key.replace(/_/g, ' ').replace(/q\d+\s*/i, '')}
                                        </p>
                                        <p className="text-gray-600">
                                          {Array.isArray(value) ? value.join(', ') : value}
                                        </p>
                                      </div>
                                    ))
                                  )}
                                  
                                  {/* Admin notes section */}
                                  <div className="pt-4 border-t border-sand">
                                    <Label className="text-sm font-medium">Admin Notes</Label>
                                    <div className="flex gap-2 mt-2">
                                      <Textarea
                                        value={applicationNote}
                                        onChange={(e) => setApplicationNote(e.target.value)}
                                        placeholder="Add a note about this application..."
                                        className="flex-1"
                                      />
                                      <Button onClick={() => addApplicationNote(app.id)} disabled={!applicationNote.trim()}>
                                        Add
                                      </Button>
                                    </div>
                                    {app.notes && app.notes.length > 0 && (
                                      <div className="mt-3 space-y-2">
                                        {app.notes.map((note, idx) => (
                                          <div key={idx} className="bg-gray-50 p-2 rounded text-sm">
                                            {note.content} <span className="text-gray-400">- {new Date(note.timestamp).toLocaleDateString()}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                          
                          {/* Status Actions */}
                          <div className="flex flex-wrap gap-2">
                            <Select
                              value={app.status || 'submitted'}
                              onValueChange={(value) => updateApplicationStatus(app.id, value)}
                            >
                              <SelectTrigger className="w-[140px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="submitted">Submitted</SelectItem>
                                <SelectItem value="reviewing">Reviewing</SelectItem>
                                <SelectItem value="interviewed">Interviewed</SelectItem>
                                <SelectItem value="accepted">Accepted</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                                <SelectItem value="waitlist">Waitlist</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              onClick={() => handleSendSdcLinkForCollective(app)}
                              disabled={sendingSdcLinkId === app.id}
                              style={{ backgroundColor: '#C06C54', color: '#ffffff' }}
                              data-testid={`send-sdc-payment-link-${app.id}`}
                              title="Send private SDC payment page link to this applicant"
                            >
                              {sendingSdcLinkId === app.id ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              ) : (
                                <Send className="h-4 w-4 mr-1" />
                              )}
                              SDC Payment Link
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                              onClick={() => deleteCollectiveApplication(app.id, app.contact?.name || 'Unknown')}
                              data-testid={`delete-application-${app.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Gift Retreat Applications Tab */}
          {activeTab === 'gift-applications' && (
            <AdminGiftApplicationsTab />
          )}

          {activeTab === 'affiliates' && (
            <AdminAffiliatesTab />
          )}

      </div>

      {/* Add to Retreat Dialog */}
      <Dialog open={showRetreatDialog} onOpenChange={setShowRetreatDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palmtree className="h-5 w-5 text-teal-600" />
              Add to Costa Rica Retreat
            </DialogTitle>
          </DialogHeader>

          {retreatResult ? (
            // Success state
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">Successfully Added!</span>
                </div>
                <p className="text-sm text-green-600">
                  {retreatUser?.full_name} has been added to the Costa Rica retreat.
                </p>
              </div>

              {retreatResult.welcome_url && (
                <div>
                  <Label className="text-xs text-gray-500">Welcome Page URL</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-xs break-all">
                      {retreatResult.welcome_url}
                    </code>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => copyToClipboard(retreatResult.welcome_url)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              <Button 
                className="w-full bg-forest hover:bg-forest/90"
                onClick={() => {
                  setRetreatResult(null);
                  setRetreatUser(null);
                  setShowRetreatDialog(false);
                }}
              >
                Done
              </Button>
            </div>
          ) : (
            // Form state
            <div className="space-y-4">
              {retreatUser && (
                <div className="bg-sand/30 p-3 rounded-lg">
                  <p className="font-medium text-forest">{retreatUser.full_name}</p>
                  <p className="text-sm text-gray-500">{retreatUser.email}</p>
                </div>
              )}

              <div>
                <Label>Room Type</Label>
                <Select
                  value={retreatForm.room_type}
                  onValueChange={(value) => setRetreatForm(prev => ({ ...prev, room_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROOM_TYPES.map((room) => (
                      <SelectItem key={room.id} value={room.id}>
                        {room.name} - {room.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Crown className="h-4 w-4 text-amber-600" />
                  <Label className="text-sm font-medium">VIP Experience</Label>
                </div>
                <Switch
                  checked={retreatForm.is_vip}
                  onCheckedChange={(checked) => setRetreatForm(prev => ({ ...prev, is_vip: checked }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-sm">Send Welcome Email & SMS</Label>
                <Switch
                  checked={retreatForm.send_notifications}
                  onCheckedChange={(checked) => setRetreatForm(prev => ({ ...prev, send_notifications: checked }))}
                />
              </div>

              <Button 
                className="w-full bg-teal-600 hover:bg-teal-700"
                onClick={handleAddToRetreat}
                disabled={addingToRetreat}
              >
                {addingToRetreat ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding to Retreat...
                  </>
                ) : (
                  <>
                    <Palmtree className="h-4 w-4 mr-2" />
                    Add to Retreat
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
};

export default AdminPage;
